import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Send, Lock, Flag, Ban } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { messagesApi, blocksApi, community as commApi } from '../services/api';
import { toast } from '../hooks/use-toast';

export function JoinPromptDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white" data-testid="join-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock size={18} className="text-green-400" /> Join the conversation</DialogTitle>
          <DialogDescription className="text-gray-300">Become a free member to post, comment, like, and message other members of the PBB community.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2">
          <Link to="/signup" data-testid="join-prompt-signup" className="flex-1 text-center px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 font-semibold">Join free</Link>
          <Link to="/login?redirect=/community" data-testid="join-prompt-login" className="flex-1 text-center px-4 py-2.5 rounded-lg border border-[#2a2a2a] hover:border-green-500">Sign in</Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BlockConfirmDialog({ target, onClose, onBlocked }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await blocksApi.block(target.userId);
      toast({ title: `${target.name} is blocked`, description: "You won't see each other's posts, comments, or messages." });
      onBlocked?.(target);
      onClose();
    } catch (err) {
      toast({ title: 'Block failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban size={18} className="text-red-400" /> Block {target?.name}?</DialogTitle>
          <DialogDescription className="text-gray-300">
            You won't be able to message each other, and you'll stop seeing each other's posts and comments. {target?.name} will be told they were blocked. You can unblock them any time from Messages.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-[#2a2a2a]" data-testid="block-cancel">Cancel</button>
          <button onClick={confirm} disabled={busy} className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="block-confirm">
            {busy && <Loader2 className="animate-spin" size={14} />} Block
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReportDialog({ target, onClose, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [alsoBlock, setAlsoBlock] = useState(true);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!target) return;
    setReason(''); setDetails(''); setAlsoBlock(true);
    if (!reasons.length) commApi.reportReasons().then(setReasons).catch(() => {});
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!reason) return toast({ title: 'Pick a reason' });
    setBusy(true);
    try {
      await commApi.report({ userId: target.userId, reason, details, alsoBlock,
        postId: target.postId || null, commentId: target.commentId || null, messageId: target.messageId || null });
      toast({ title: 'Report sent', description: alsoBlock ? `Thanks. ${target.name} is also blocked.` : 'Thanks. Our team will review it.' });
      onDone?.(alsoBlock);
      onClose();
    } catch (err) {
      toast({ title: 'Report failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flag size={18} className="text-red-400" /> Report {target?.name}</DialogTitle>
          <DialogDescription className="text-gray-300">Our admin team will review this. {target?.name} won't be told who reported them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2" data-testid="report-reasons">
          {reasons.map(r => (
            <label key={r} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${reason === r ? 'border-red-400 bg-red-500/10' : 'border-[#2a2a2a]'}`}>
              <input type="radio" name="report-reason" checked={reason === r} onChange={() => setReason(r)} data-testid={`report-reason-${r.split(' ')[0].toLowerCase()}`} /> {r}
            </label>
          ))}
        </div>
        <textarea rows={3} value={details} onChange={e => setDetails(e.target.value)} maxLength={1000}
          className="pbb-input" placeholder="Anything else we should know? (optional)" data-testid="report-details" />
        <label className="flex items-center gap-2 text-sm text-gray-200">
          <input type="checkbox" checked={alsoBlock} onChange={e => setAlsoBlock(e.target.checked)} data-testid="report-also-block" /> Also block {target?.name}
        </label>
        <button onClick={submit} disabled={busy} data-testid="report-submit"
          className="px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 className="animate-spin" size={14} />} Send report
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function MessageDialog({ post, onClose }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await messagesApi.send(post.userId, text.trim(), post.id);
      toast({ title: `Message sent to ${post.user}`, description: 'Only you and they can see it.' });
      setText('');
      onClose();
      navigate(`/messages?with=${post.userId}`);
    } catch (err) {
      toast({ title: 'Message failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!post} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle>Private message to {post?.user}</DialogTitle>
          <DialogDescription className="text-gray-300">Only you and {post?.user} will see this message.</DialogDescription>
        </DialogHeader>
        <textarea rows={4} value={text} onChange={e => setText(e.target.value)} maxLength={2000}
          className="pbb-input" placeholder="Write your message..." data-testid="message-dialog-input" />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="message-dialog-send"
          className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send privately
        </button>
      </DialogContent>
    </Dialog>
  );
}
